import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BellRing, Plus, Trash2, Users, CalendarClock, Check, Repeat } from "lucide-react";

/**
 * Alarms — anyone can create an alarm for themselves, managers/admins can
 * assign to other team members. Alarms ring on every assignee's device the
 * moment fire_at passes (see AlarmOverlay). This page is the create + manage
 * surface: form on the left, list of upcoming/past alarms on the right.
 */
export default function AlarmsPage() {
  const { user } = useAuth();
  const canAssignOthers = !!(user?.is_super_admin || user?.is_admin || user?.role === "manager");

  const [alarms, setAlarms] = useState([]);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [when, setWhen] = useState("");   // datetime-local string
  const [recurrence, setRecurrence] = useState("none");   // none | daily | weekly | monthly
  const [assignees, setAssignees] = useState([]);   // user ids

  const load = useCallback(async () => {
    try {
      const [{ data: rows }, { data: mems }] = await Promise.all([
        api.get("/alarms"),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setAlarms(Array.isArray(rows) ? rows : []);
      setMembers(Array.isArray(mems) ? mems : []);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load alarms"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const now = new Date();
  // Recurring alarms are always "Upcoming" — show them with their next occurrence.
  // One-shot alarms move to "Past" once fire_at has elapsed.
  const nextOccurrenceOf = (a) => {
    if (!a.recurrence || a.recurrence === "none") return new Date(a.fire_at);
    let d = new Date(a.fire_at);
    // Guard: cap at 2000 iterations so a malformed record never spins.
    for (let i = 0; i < 2000 && d <= now; i++) {
      if (a.recurrence === "daily")   d.setDate(d.getDate() + 1);
      else if (a.recurrence === "weekly")  d.setDate(d.getDate() + 7);
      else if (a.recurrence === "monthly") d.setMonth(d.getMonth() + 1);
      else break;
    }
    return d;
  };
  const upcoming = useMemo(() => {
    return alarms
      .filter((a) => (a.recurrence && a.recurrence !== "none") || new Date(a.fire_at) > now)
      .map((a) => ({ ...a, _sort_at: nextOccurrenceOf(a) }))
      .sort((x, y) => x._sort_at - y._sort_at);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarms, now.getTime()]);
  const past = useMemo(() => alarms.filter((a) => (!a.recurrence || a.recurrence === "none") && new Date(a.fire_at) <= now), [alarms, now]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim()) { toast.error("Title required"); return; }
    if (!when) { toast.error("Pick a time"); return; }
    setSaving(true);
    try {
      const fireAt = new Date(when).toISOString();
      await api.post("/alarms", {
        title: title.trim(),
        description: description.trim(),
        fire_at: fireAt,
        recurrence,
        assignee_ids: canAssignOthers ? assignees : [],   // backend auto-adds self
      });
      toast.success(recurrence === "none" ? "Alarm scheduled" : `Alarm scheduled — repeats ${recurrence}`);
      setTitle(""); setDescription(""); setWhen(""); setRecurrence("none"); setAssignees([]);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create alarm");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this alarm? Everyone assigned will stop getting it.")) return;
    try { await api.delete(`/alarms/${id}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const toggleAssignee = (uid) => {
    setAssignees((cur) => cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]);
  };

  const nowLocalIso = () => {
    // Default to "in 5 minutes" so the picker isn't blank
    const d = new Date(); d.setMinutes(d.getMinutes() + 5);
    return d.toISOString().slice(0, 16);
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-5" data-testid="alarms-page">
      <div>
        <div className="text-[0.7rem] uppercase tracking-[0.3em] text-white/40 flex items-center gap-2"><BellRing size={12} /> Reminders</div>
        <h1 className="text-3xl sm:text-4xl font-display tracking-tight">Alarms</h1>
        <p className="text-sm text-white/50 mt-1">
          Set a ringing reminder for yourself — or {canAssignOthers ? "any of your teammates" : "let a manager set one for you"}. The alarm rings on every assignee's device until they stop it.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* Create form */}
        <form onSubmit={submit} className="lg:col-span-1 rounded-lg border border-white/10 bg-black/25 p-4 space-y-3">
          <div className="text-xs uppercase tracking-widest text-white/60 font-mono">New alarm</div>
          <div>
            <label className="text-[0.7rem] uppercase tracking-widest text-white/50 font-mono">Title</label>
            <Input
              data-testid="alarm-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Client review call"
              className="bg-black/40 border-white/10 mt-1"
            />
          </div>
          <div>
            <label className="text-[0.7rem] uppercase tracking-widest text-white/50 font-mono">When</label>
            <Input
              data-testid="alarm-when-input"
              type="datetime-local"
              value={when || nowLocalIso()}
              onChange={(e) => setWhen(e.target.value)}
              className="bg-black/40 border-white/10 mt-1"
            />
          </div>
          <div>
            <label className="text-[0.7rem] uppercase tracking-widest text-white/50 font-mono flex items-center gap-1">
              <Repeat size={11} /> Repeats
            </label>
            <div className="mt-1 grid grid-cols-4 gap-1" data-testid="alarm-recurrence">
              {[
                { key: "none",    label: "Once" },
                { key: "daily",   label: "Daily" },
                { key: "weekly",  label: "Weekly" },
                { key: "monthly", label: "Monthly" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setRecurrence(opt.key)}
                  data-testid={`alarm-recurrence-${opt.key}`}
                  className={`px-2 py-1.5 rounded text-xs border transition ${
                    recurrence === opt.key
                      ? "bg-[#E11D2E]/20 border-[#E11D2E]/60 text-white"
                      : "bg-black/40 border-white/10 text-white/60 hover:text-white hover:border-white/30"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {recurrence !== "none" && (
              <div className="text-[0.7rem] text-white/40 font-mono mt-1">
                Rings on schedule; dismissing it rolls to the next occurrence automatically.
              </div>
            )}
          </div>
          <div>
            <label className="text-[0.7rem] uppercase tracking-widest text-white/50 font-mono">Description (optional)</label>
            <Textarea
              data-testid="alarm-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Agenda, join link, etc."
              className="bg-black/40 border-white/10 mt-1 resize-none"
            />
          </div>
          {canAssignOthers && (
            <div>
              <label className="text-[0.7rem] uppercase tracking-widest text-white/50 font-mono flex items-center gap-1">
                <Users size={11} /> Also alert…
              </label>
              <div className="mt-1 max-h-40 overflow-y-auto rounded border border-white/10 bg-black/40 p-2 space-y-1" data-testid="alarm-assignees">
                {members.filter((m) => m.id !== user?.id).map((m) => (
                  <button
                    type="button"
                    key={m.id}
                    onClick={() => toggleAssignee(m.id)}
                    className={`w-full flex items-center justify-between px-2 py-1 rounded text-xs hover:bg-white/5 transition ${assignees.includes(m.id) ? "text-emerald-300" : "text-white/70"}`}
                    data-testid={`alarm-assignee-${m.id}`}
                  >
                    <span className="truncate">{m.name}</span>
                    {assignees.includes(m.id) && <Check size={12} />}
                  </button>
                ))}
                {members.length === 0 && <div className="text-xs text-white/40">No teammates loaded.</div>}
              </div>
              <div className="text-[0.7rem] text-white/40 font-mono mt-1">You'll be added automatically.</div>
            </div>
          )}
          <Button
            type="submit"
            disabled={saving}
            data-testid="alarm-save-btn"
            className="w-full bg-[#E11D2E] hover:bg-[#F03041] rounded-md"
          >
            <Plus size={14} className="mr-1.5" /> {saving ? "Scheduling…" : "Schedule alarm"}
          </Button>
        </form>

        {/* Upcoming + past list */}
        <div className="lg:col-span-2 space-y-4">
          <AlarmList title="Upcoming" alarms={upcoming} onDelete={remove} canDelete={(a) => a.created_by === user?.id || user?.is_super_admin} />
          <AlarmList title="Past & dismissed" alarms={past} onDelete={remove} canDelete={(a) => a.created_by === user?.id || user?.is_super_admin} muted />
        </div>
      </div>
    </div>
  );
}

function AlarmList({ title, alarms, onDelete, canDelete, muted }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/25 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarClock size={12} className="text-white/50" />
          <div className="text-xs uppercase tracking-widest text-white/60 font-mono">{title}</div>
        </div>
        <div className="text-[0.7rem] text-white/40 font-mono">{alarms.length} alarm{alarms.length === 1 ? "" : "s"}</div>
      </div>
      {alarms.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-white/40" data-testid={`alarm-list-empty-${title}`}>
          {muted ? "Nothing yet." : "Nothing scheduled."}
        </div>
      ) : (
        <ul className="divide-y divide-white/5">
          {alarms.map((a) => {
            const when = new Date(a._sort_at || a.fire_at);
            return (
              <li key={a.id} className={`px-4 py-2.5 flex items-center gap-3 hover:bg-white/[0.03] ${muted ? "opacity-70" : ""}`} data-testid={`alarm-row-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate flex items-center gap-2">
                    <span className="truncate">{a.title}</span>
                    {a.recurrence && a.recurrence !== "none" && (
                      <span
                        data-testid={`alarm-recurrence-badge-${a.id}`}
                        className="shrink-0 inline-flex items-center gap-1 text-[0.65rem] uppercase tracking-widest bg-[#E11D2E]/15 border border-[#E11D2E]/40 text-red-200 rounded px-1.5 py-0.5 font-mono"
                      >
                        <Repeat size={9} /> {a.recurrence}
                      </span>
                    )}
                  </div>
                  <div className="text-[0.7rem] text-white/50 font-mono">
                    {when.toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} ·{" "}
                    from {a.created_by_name} ·{" "}
                    {a.assignee_ids.length} recipient{a.assignee_ids.length === 1 ? "" : "s"}
                  </div>
                  {a.description && <div className="text-xs text-white/60 mt-0.5 truncate">{a.description}</div>}
                </div>
                {canDelete(a) && (
                  <button
                    onClick={() => onDelete(a.id)}
                    data-testid={`alarm-delete-${a.id}`}
                    className="text-white/40 hover:text-red-400 p-1.5 rounded shrink-0"
                    title="Delete alarm"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
